<template>
  <div class="profile-dropdown-wrapper">
    <va-dropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <va-button preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <va-icon :size="32" name="mso-account_circle" color="primary" />
          </span>
        </va-button>
      </template>
      <va-dropdown-content
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <va-list v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <va-list-item
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleItemClick(item)"
          >
            <va-icon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </va-list-item>
          <va-list-separator v-if="group.separator" class="mx-3 my-2" />
          <va-list-item class="menu-item px-4 text-base cursor-pointer h-8" @click="logout">
            <va-icon name="mso-logout" class="pr-1" color="secondary" /> Logout
          </va-list-item>
        </va-list>
      </va-dropdown-content>
    </va-dropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { authStore } from '../../../../stores/auth'
import { useRouter } from 'vue-router'
import { disconnectAllWebSockets } from '../../../../services/websocketUtils'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  onClick?: () => void
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'usuarioLogado',
            icon: 'mso-account_circle',
          },
          {
            name: 'automations_processes',
            icon: 'mso-sync',
          },
          // {
          //   name: 'settings',
          //   to: 'settings',
          //   icon: 'mso-settings',
          // },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const resolveLinkAttribute = (item: ProfileListItem) => {
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}

import { EventBus } from '../../../../services/eventBus.js'

const handleItemClick = (item: ProfileListItem) => {
  // Links ou rotas devem navegar normalmente
  if (item.to || item.href) return

  // Ação customizada: abrir processamento de automações (coexistence)
  if (item.name === 'automations_processes') {
    EventBus.emit('coexistence-sync-open')
    isShown.value = false
  }
  // Itens com onClick opcional
  if (typeof item.onClick === 'function') {
    item.onClick()
    isShown.value = false
  }
}

const auth = authStore()
const { push } = useRouter()

const logout = async () => {
  try {
    disconnectAllWebSockets()
    auth.removeToken()
    push({ name: 'login' })
  } catch (error) {
    console.error(error)
  }
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
